import type { Locale } from "@saas/internationalization";

export interface Copy {
  nav: string[];
  hero: string[];
  benefits: string[];
  features: string[];
  useCases: string[];
  pricing: string[];
  plans: {
    name: string;
    monthlyPriceMinor: number;
    description: string;
    features: string[];
  }[];
  faq: { q: string; a: string }[];
  cta: string[];
  footer: string;
  meta: { title: string; description: string };
}

const plans = (
  names: [string, string, string],
  descriptions: [string, string, string],
  features: [string[], string[], string[]],
) => [
  {
    name: names[0],
    monthlyPriceMinor: 1_990,
    description: descriptions[0],
    features: features[0],
  },
  {
    name: names[1],
    monthlyPriceMinor: 2_990,
    description: descriptions[1],
    features: features[1],
  },
  {
    name: names[2],
    monthlyPriceMinor: 4_990,
    description: descriptions[2],
    features: features[2],
  },
];

export const copy: Record<Locale, Copy> = {
  en: {
    nav: [
      "Why TrackBoss",
      "What it organizes",
      "For your routine",
      "Plans",
      "Questions",
      "Sign in",
      "Create account",
    ],
    hero: [
      "YOUR FINANCIAL LIFE, IN CONTEXT",
      "See what you have, what is coming in, and what needs attention.",
      "TrackBoss brings personal finances, business cash flow, goals, and investments into one organized view — while keeping each workspace separate.",
      "Join the pilot",
      "Explore what TrackBoss organizes",
      "A closed pilot for people who manage personal finances, a business, and investments",
    ],
    benefits: [
      "Clarity before complexity",
      "A financial routine you can actually follow.",
      "One connected picture",
      "Bring cash flow, commitments, goals, and investments together without blending personal and business assets.",
      "Your data, with context",
      "Record entries manually or import CSV and OFX files; keep origin and reference dates visible.",
      "Decisions stay yours",
      "TrackBoss organizes information and shows scenarios. It does not recommend investments or act on your behalf.",
    ],
    features: [
      "What the pilot focuses on",
      "Start with the financial information that needs a home.",
      "Cash flow and categories",
      "Keep income, expenses, due dates, and monthly budgets organized.",
      "Goals with a plan",
      "Follow a trip, reserve, purchase, or income goal with a target and date.",
      "Investments in perspective",
      "Record positions, contributions, and proceeds alongside the rest of your financial life.",
      "Personal and business workspaces",
      "Keep PF and PJ separate, then consolidate only authorized information.",
    ],
    useCases: [
      "Built around real routines",
      "TrackBoss adapts to the way your finances already move.",
      "Personal finances",
      "Plan your month and understand how each choice affects your goals.",
      "Personal plus business",
      "Keep personal and company cash flow distinct while seeing both obligations.",
      "People who invest",
      "Place positions and proceeds in the context of budget, goals, and planned cash flow.",
    ],
    pricing: [
      "Pilot plans",
      "Choose the scope that fits your financial routine.",
      "Monthly billing",
      "Annual billing · pay for 10 months",
      "Choose",
      "Recommended for the pilot",
      "per month",
    ],
    plans: plans(
      ["Essential PF", "Complete PF", "Business + Investments"],
      [
        "For organizing your personal financial life.",
        "For a fuller view of personal finances and investments.",
        "For those who manage personal finances, a business, and investments.",
      ],
      [
        [
          "Budget and goals",
          "Manual, CSV, and OFX imports",
          "Personal workspace",
        ],
        [
          "Everything in Essential",
          "Portfolios and projections",
          "One consented connection when available",
        ],
        [
          "Personal and one business workspace",
          "Business contracts and forecasts",
          "Basic permissions",
        ],
      ],
    ),
    faq: [
      {
        q: "Is TrackBoss financial advice?",
        a: "No. TrackBoss organizes information and presents scenarios. It does not recommend, buy, sell, or manage investments for you.",
      },
      {
        q: "Do I need to connect my bank?",
        a: "No. The pilot starts with manual entries and CSV or OFX imports. Bank connectivity is offered only when available and consented to.",
      },
      {
        q: "Will personal and business finances be mixed?",
        a: "No. Workspaces are separate; any consolidated view is deliberate and limited to authorized access.",
      },
    ],
    cta: [
      "Make your financial information easier to understand.",
      "Start with the routine you have today and build a clearer view of what comes next.",
      "Join the pilot",
    ],
    footer: "© 2026 TrackBoss. All rights reserved.",
    meta: {
      title: "TrackBoss — Your finances, in context",
      description:
        "Organize personal finances, business cash flow, goals, and investments in one clear view.",
    },
  },
  "pt-BR": {
    nav: [
      "Por que TrackBoss",
      "O que organiza",
      "Para sua rotina",
      "Planos",
      "Dúvidas",
      "Entrar",
      "Criar conta",
    ],
    hero: [
      "SUA VIDA FINANCEIRA, EM CONTEXTO",
      "Veja o que você tem, o que entra e o que merece atenção.",
      "O TrackBoss reúne finanças pessoais, fluxo de caixa da PJ, metas e investimentos em uma visão organizada — mantendo cada espaço separado.",
      "Entrar no piloto",
      "Conhecer o TrackBoss",
      "Um piloto fechado para quem cuida da vida financeira, da PJ e dos investimentos",
    ],
    benefits: [
      "Clareza antes da complexidade",
      "Uma rotina financeira que você consegue acompanhar.",
      "Uma visão conectada",
      "Reúna fluxo de caixa, compromissos, metas e investimentos sem misturar automaticamente o patrimônio pessoal e o da empresa.",
      "Seus dados, com contexto",
      "Registre lançamentos manualmente ou importe CSV e OFX; mantenha origem e data-base visíveis.",
      "As decisões continuam suas",
      "O TrackBoss organiza informações e mostra cenários. Ele não recomenda investimentos nem toma decisões por você.",
    ],
    features: [
      "O foco do piloto",
      "Comece pelas informações financeiras que precisam de um lugar.",
      "Fluxo de caixa e categorias",
      "Organize receitas, despesas, vencimentos e orçamento mensal.",
      "Metas com um plano",
      "Acompanhe viagem, reserva, compra ou renda desejada com valor e prazo.",
      "Investimentos em perspectiva",
      "Registre posições, aportes e proventos ao lado do restante da sua vida financeira.",
      "Espaços PF e PJ",
      "Mantenha pessoa física e empresa separadas; consolide apenas o que puder acompanhar.",
    ],
    useCases: [
      "Feito para rotinas reais",
      "O TrackBoss acompanha a forma como a sua vida financeira já se movimenta.",
      "Finanças pessoais",
      "Planeje o mês e entenda o efeito das escolhas nas suas metas.",
      "Pessoa física e PJ",
      "Separe o caixa pessoal do empresarial sem perder de vista as obrigações de cada um.",
      "Para quem investe",
      "Coloque posições e proventos no contexto do orçamento, das metas e do fluxo de caixa.",
    ],
    pricing: [
      "Planos do piloto",
      "Escolha o escopo que combina com a sua rotina financeira.",
      "Cobrança mensal",
      "Cobrança anual · pague 10 meses",
      "Escolher",
      "Recomendado para o piloto",
      "por mês",
    ],
    plans: plans(
      ["Essencial PF", "Completo PF", "PJ + Investimentos"],
      [
        "Para organizar a sua vida financeira pessoal.",
        "Para uma visão completa das finanças e investimentos pessoais.",
        "Para quem cuida da PF, de uma empresa e dos investimentos.",
      ],
      [
        [
          "Orçamento e metas",
          "Importação manual, CSV e OFX",
          "1 espaço pessoal",
        ],
        [
          "Tudo do Essencial",
          "Carteiras e projeções",
          "1 conexão consentida quando disponível",
        ],
        ["PF e 1 empresa", "Contratos e prévias PJ", "Permissões básicas"],
      ],
    ),
    faq: [
      {
        q: "O TrackBoss dá recomendação financeira?",
        a: "Não. O TrackBoss organiza informações e apresenta cenários. Ele não recomenda, compra, vende nem administra investimentos em seu nome.",
      },
      {
        q: "Preciso conectar meu banco?",
        a: "Não. O piloto começa com lançamentos manuais e importação de CSV ou OFX. A conectividade só será oferecida quando estiver disponível e for consentida.",
      },
      {
        q: "Minhas finanças pessoais e da PJ serão misturadas?",
        a: "Não. Os espaços PF e PJ são separados; uma visão consolidada é intencional e limitada ao seu acesso.",
      },
    ],
    cta: [
      "Deixe suas informações financeiras mais fáceis de entender.",
      "Comece pela rotina que você já tem e construa uma visão mais clara do que vem pela frente.",
      "Entrar no piloto",
    ],
    footer: "© 2026 TrackBoss. Todos os direitos reservados.",
    meta: {
      title: "TrackBoss — Sua vida financeira, em contexto",
      description:
        "Organize finanças pessoais, fluxo de caixa da PJ, metas e investimentos em uma visão clara.",
    },
  },
  "pt-PT": {
    nav: [
      "Porquê TrackBoss",
      "O que organiza",
      "Para a sua rotina",
      "Planos",
      "Perguntas",
      "Entrar",
      "Criar conta",
    ],
    hero: [
      "A SUA VIDA FINANCEIRA, EM CONTEXTO",
      "Veja o que tem, o que entra e o que merece atenção.",
      "O TrackBoss reúne finanças pessoais, fluxo de caixa da empresa, objetivos e investimentos numa visão organizada, mantendo cada espaço separado.",
      "Entrar no piloto",
      "Conhecer o TrackBoss",
      "Um piloto fechado para quem gere a vida financeira, uma empresa e investimentos",
    ],
    benefits: [
      "Clareza antes da complexidade",
      "Uma rotina financeira que consegue acompanhar.",
      "Uma visão ligada",
      "Reúna fluxo de caixa, compromissos, objetivos e investimentos sem misturar automaticamente o património pessoal e o da empresa.",
      "Os seus dados, com contexto",
      "Registe lançamentos manualmente ou importe CSV e OFX; mantenha origem e data de referência visíveis.",
      "As decisões continuam a ser suas",
      "O TrackBoss organiza informação e mostra cenários. Não recomenda investimentos nem toma decisões por si.",
    ],
    features: [
      "O foco do piloto",
      "Comece pelas informações financeiras que precisam de um lugar.",
      "Fluxo de caixa e categorias",
      "Organize receitas, despesas, vencimentos e orçamento mensal.",
      "Objetivos com um plano",
      "Acompanhe uma viagem, reserva ou compra com valor e prazo.",
      "Investimentos em perspetiva",
      "Registe posições, reforços e rendimentos junto do resto da sua vida financeira.",
      "Espaços pessoais e empresariais",
      "Mantenha pessoa singular e empresa separadas; consolide apenas o que pode acompanhar.",
    ],
    useCases: [
      "Feito para rotinas reais",
      "TrackBoss acompanha a sua vida financeira.",
      "Finanças pessoais",
      "Planeie o mês e compreenda as suas metas.",
      "Pessoal e empresa",
      "Separe dinheiro pessoal e empresarial.",
      "Para quem investe",
      "Coloque posições e rendimentos no contexto da sua rotina.",
    ],
    pricing: [
      "Planos do piloto",
      "Escolha o âmbito adequado.",
      "Faturação mensal",
      "Faturação anual · pague 10 meses",
      "Escolher",
      "Recomendado para o piloto",
      "por mês",
    ],
    plans: plans(
      ["Essencial PF", "Completo PF", "PJ + Investimentos"],
      [
        "Para a vida financeira pessoal.",
        "Para finanças e investimentos pessoais.",
        "Para vida pessoal, empresa e investimentos.",
      ],
      [
        [
          "Orçamento e objetivos",
          "Importação manual, CSV e OFX",
          "1 espaço pessoal",
        ],
        [
          "Tudo do Essencial",
          "Carteiras e projeções",
          "1 ligação consentida quando disponível",
        ],
        ["PF e 1 empresa", "Contratos e previsões", "Permissões básicas"],
      ],
    ),
    faq: [
      {
        q: "TrackBoss dá recomendações financeiras?",
        a: "Não. Organiza informação e apresenta cenários, mas não gere investimentos em seu nome.",
      },
      {
        q: "Tenho de ligar o banco?",
        a: "Não. O piloto começa com lançamentos manuais e CSV ou OFX.",
      },
      {
        q: "As finanças serão misturadas?",
        a: "Não. Os espaços são separados e as consolidações são intencionais.",
      },
    ],
    cta: [
      "Torne a sua informação financeira mais fácil de compreender.",
      "Comece pela rotina que já tem.",
      "Entrar no piloto",
    ],
    footer: "© 2026 TrackBoss. Todos os direitos reservados.",
    meta: {
      title: "TrackBoss — A sua vida financeira, em contexto",
      description:
        "Finanças pessoais, empresa, objetivos e investimentos numa visão clara.",
    },
  },
  es: {
    nav: [
      "Por qué TrackBoss",
      "Qué organiza",
      "Para tu rutina",
      "Planes",
      "Preguntas",
      "Ingresar",
      "Crear cuenta",
    ],
    hero: [
      "TU VIDA FINANCIERA, EN CONTEXTO",
      "Ve lo que tienes, lo que entra y lo que merece atención.",
      "TrackBoss reúne finanzas personales, flujo de caja del negocio, metas e inversiones en una vista organizada, manteniendo cada espacio separado.",
      "Unirme al piloto",
      "Conocer TrackBoss",
      "Un piloto cerrado para quienes gestionan sus finanzas, un negocio e inversiones",
    ],
    benefits: [
      "Claridad antes que complejidad",
      "Una rutina financiera que puedes seguir.",
      "Una visión conectada",
      "Reúne flujo de caja, compromisos, metas e inversiones sin mezclar automáticamente el patrimonio personal y empresarial.",
      "Tus datos, con contexto",
      "Registra movimientos manualmente o importa CSV y OFX; conserva el origen y la fecha de referencia.",
      "Las decisiones siguen siendo tuyas",
      "TrackBoss organiza información y muestra escenarios. No recomienda inversiones ni toma decisiones por ti.",
    ],
    features: [
      "El foco del piloto",
      "Empieza por la información financiera que necesita un lugar.",
      "Flujo de caja y categorías",
      "Organiza ingresos, gastos, vencimientos y presupuesto mensual.",
      "Metas con un plan",
      "Sigue una meta con importe y plazo.",
      "Inversiones en perspectiva",
      "Registra posiciones, aportes y rendimientos junto al resto de tu vida financiera.",
      "Espacios personales y de negocio",
      "Mantén persona física y empresa separadas; consolida solo información autorizada.",
    ],
    useCases: [
      "Para rutinas reales",
      "TrackBoss acompaña tus finanzas.",
      "Finanzas personales",
      "Planifica el mes y tus metas.",
      "Persona física y negocio",
      "Separa dinero personal y empresarial.",
      "Para quien invierte",
      "Pon inversiones en el contexto de tu presupuesto y metas.",
    ],
    pricing: [
      "Planes del piloto",
      "Elige el alcance adecuado.",
      "Facturación mensual",
      "Facturación anual · paga 10 meses",
      "Elegir",
      "Recomendado para el piloto",
      "por mes",
    ],
    plans: plans(
      ["Esencial PF", "Completo PF", "PJ + Inversiones"],
      [
        "Para tus finanzas personales.",
        "Para finanzas e inversiones personales.",
        "Para finanzas, negocio e inversiones.",
      ],
      [
        [
          "Presupuesto y metas",
          "Importación manual, CSV y OFX",
          "1 espacio personal",
        ],
        [
          "Todo lo Esencial",
          "Carteras y proyecciones",
          "1 conexión consentida cuando esté disponible",
        ],
        ["PF y 1 empresa", "Contratos y previsiones", "Permisos básicos"],
      ],
    ),
    faq: [
      {
        q: "¿TrackBoss da recomendaciones financieras?",
        a: "No. Organiza información y muestra escenarios; no administra inversiones por ti.",
      },
      {
        q: "¿Necesito conectar mi banco?",
        a: "No. El piloto empieza con movimientos manuales y CSV u OFX.",
      },
      {
        q: "¿Se mezclarán las finanzas?",
        a: "No. Los espacios son separados y las consolidaciones son intencionales.",
      },
    ],
    cta: [
      "Haz que tu información financiera sea más fácil de entender.",
      "Empieza con la rutina que ya tienes.",
      "Unirme al piloto",
    ],
    footer: "© 2026 TrackBoss. Todos los derechos reservados.",
    meta: {
      title: "TrackBoss — Tu vida financiera, en contexto",
      description:
        "Finanzas personales, negocio, metas e inversiones en una vista clara.",
    },
  },
};
